/**
 * Virginia medication-coverage plan grid (English source of truth).
 *
 * Lives in its own module so both the English route and the Spanish
 * translation layer can read the same rows; the Spanish table merges its
 * overrides on top of these values and falls back to English for anything
 * that has not been translated yet.
 */
export interface PlanRow {
  plan: string;
  bmi: string;
  bmiNote?: string;
  lifestyle: string;
  lifestyleNote?: string;
  step: string;
  stepNote?: string;
  initial: string;
  initialNote?: string;
  renewal: string;
  renewalNote?: string;
}

/** Fields that may be translated per language. */
export type PlanField = keyof PlanRow;

export const EN_PLANS: PlanRow[] = [
  {
    plan: "Virginia Medicaid (Cardinal Care)",
    bmi: "Over 40 — or over 37 with high cholesterol, high blood pressure, or type 2 diabetes",
    bmiNote: "BMI is body mass index. Cardinal Care uses these higher thresholds and requires a weight-related condition for the lower BMI threshold.",
    lifestyle: "Nutritional counseling plus physical activity program",
    lifestyleNote: "Documentation from a recognized program or provider is usually required, not just a personal log.",
    step: "Yes — a non-GLP-1 weight-loss drug",
    stepNote: "Step therapy means you must try a cheaper or older medication first and show it did not work or was not tolerated.",
    initial: "6 months",
    initialNote: "Initial approval is typically granted for a 6-month period, then renewal requires proof of benefit.",
    renewal: "At least 5% weight loss",
    renewalNote: "5% of your starting body weight must be lost to renew; this is a common insurer benchmark.",
  },
  {
    plan: "Virginia state employee plans (COVA)",
    bmi: "35 or higher (raised from 27/30 on July 1, 2026)",
    bmiNote: "COVA raised its BMI threshold in 2026. Employer-specific COVA plans may still differ.",
    lifestyle: "Standard prior authorization guidelines apply",
    step: "Not specified in plan materials",
    initial: "Per prior authorization",
    initialNote: "Coverage is decided case-by-case through the plan's prior-authorization process.",
    renewal: "Per prior authorization",
  },
  {
    plan: "Sentara Health Plans",
    bmi: "30 — or 27 with a qualifying condition",
    bmiNote: "A qualifying condition is a weight-related health problem such as diabetes, high blood pressure, or sleep apnea.",
    lifestyle: "Weight-loss plan within the past 6 months, continuing",
    lifestyleNote: "The plan must have started recently and still be active when you apply.",
    step: "No",
    stepNote: "Sentara's published criteria do not currently require trying another weight-loss drug first.",
    initial: "7 months",
    initialNote: "Sentara commonly authorizes an initial 7-month supply before requiring renewal documentation.",
    renewal: "At least 5% weight loss, then maintenance",
    renewalNote: "After proving 5% loss, ongoing approval may shift to a maintenance schedule.",
  },
  {
    plan: "UnitedHealthcare (if employer elects coverage)",
    bmi: "30 — or 27 with a qualifying condition",
    bmiNote: "Employer-sponsored UnitedHealthcare plans only cover weight-loss medication if the employer opts in.",
    lifestyle: "Used alongside lifestyle modification",
    step: "No",
    initial: "Saxenda 4 mo; Wegovy 5 mo; Zepbound 6 mo",
    initialNote: "Initial approval length varies by drug: Saxenda 4 months, Wegovy 5 months, Zepbound 6 months.",
    renewal: "4% Saxenda; 5% Wegovy and Zepbound",
    renewalNote: "Saxenda requires at least 4% weight loss; Wegovy and Zepbound require at least 5%.",
  },
  {
    plan: "Cigna",
    bmi: "30 — or 27 with a qualifying condition",
    lifestyle: "At least 3 months of behavioral and dietary change",
    lifestyleNote: "Changes must be documented by a clinician; self-reported dieting usually does not count.",
    step: "No",
    initial: "Zepbound and Wegovy 8 mo; Saxenda 4 mo",
    initialNote: "Cigna authorizes Zepbound and Wegovy for 8 months initially, Saxenda for 4 months.",
    renewal: "5% Zepbound and Wegovy; 4% Saxenda",
    renewalNote: "Renewal thresholds differ by medication: 5% for Zepbound/Wegovy, 4% for Saxenda.",
  },
  {
    plan: "TRICARE",
    bmi: "30 — or 27 to 29 with a qualifying condition",
    bmiNote: "TRICARE allows a lower BMI range (27–29) when a qualifying condition is documented.",
    lifestyle: "At least 6 months, documented in your chart",
    lifestyleNote: "Six consecutive months of documented diet, exercise, and behavior changes are required.",
    step: "Yes — 3-month trial of a generic weight-loss drug",
    stepNote: "You must try a generic weight-loss drug for three months before a GLP-1 will be considered.",
    initial: "12 months",
    initialNote: "TRICARE's initial authorization is typically 12 months, longer than most commercial plans.",
    renewal: "At least 5% weight loss",
  },
  {
    plan: "Medicare GLP-1 Bridge",
    bmi: "BMI recorded; for weight loss only, with no Part D-eligible diagnosis",
    bmiNote: "This is a temporary manufacturer assistance program, not traditional Medicare Part D coverage.",
    lifestyle: "Structured nutrition and physical activity",
    step: "No",
    initial: "Runs through Dec 31, 2027",
    initialNote: "The program is scheduled to end December 31, 2027 unless extended by the manufacturer.",
    renewal: "$50/month copay",
    renewalNote: "The $50 monthly copay applies while enrolled in the bridge program.",
  },
  {
    plan: "Virginia marketplace and small-group plans",
    bmi: "Generally not covered — the state benchmark plan excludes weight-loss drugs",
    bmiNote: "Virginia's benchmark plan does not include weight-loss medication coverage, so most ACA plans follow suit.",
    lifestyle: "—",
    step: "—",
    initial: "—",
    renewal: "—",
  },
];
